import { Component, OnInit, OnDestroy } from '@angular/core';
import { ThemeService, ThemeDefinition } from '../../../Services/theme.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'app-theme-switcher',
  encapsulation: ViewEncapsulation.None,
  template: `
    <button mat-icon-button 
            [matMenuTriggerFor]="themeMenu"
            matTooltip="Change Theme"
            class="theme-toggle-btn"
            aria-label="Change application theme">
      <mat-icon>palette</mat-icon>
    </button>

    <mat-menu #themeMenu="matMenu" class="theme-menu"  [overlapTrigger]="false" panelClass="theme-menu">
      <button mat-menu-item 
              *ngFor="let theme of availableThemes"
              (click)="selectTheme(theme)"
              [class.active-theme]="isCurrentTheme(theme)"
              class="theme-menu-item">
        <mat-icon class="theme-check-icon" *ngIf="isCurrentTheme(theme)">check_circle</mat-icon>
        <mat-icon class="theme-check-icon-empty" *ngIf="!isCurrentTheme(theme)">radio_button_unchecked</mat-icon>
        <span class="theme-name">{{ theme.displayName }}</span>
        <span class="theme-description" *ngIf="theme.description">{{ theme.description }}</span>
      </button>
    </mat-menu>
  `,
  styles: [`
    .theme-toggle-btn {
      color: var(--accent);
      transition: all 0.3s ease;
    }

    .theme-toggle-btn:hover {
      transform: scale(1.1);
    }
       
    .theme-menu,
    .cdk-overlay-container .cdk-overlay-pane .mat-menu-panel {
      min-width: 220px !important;
      max-width: 300px !important;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-menu-item {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px !important;
      transition: all 0.2s ease;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-menu-item:hover {
      background-color: rgba(var(--accent), 0.1) !important;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-menu-item.active-theme {
      background-color: rgba(var(--accent), 0.15) !important;
      font-weight: 500;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-check-icon {
      color: var(--accent);
      margin-right:5px !important;
      font-size: 15px;
      width: 24px;
      height: 15px;
      overflow: visible;
      margin-top: -8px;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-check-icon-empty {
      color: var(--muted);
      margin-right:5px !important;
      font-size: 15px;
      width: 24px;
      height: 15px;
      overflow: visible;
      margin-top: -8px;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-name {
      flex: 1;
      text-align: left;
    }

    .cdk-overlay-container .cdk-overlay-pane .theme-description {
      font-size: 11px;
      color: var(--muted);
      width: 100%;
      text-align: left;
      margin-left: 10px;
      margin-top: 0px;
    }
  `]
})
export class ThemeSwitcherComponent implements OnInit, OnDestroy {
  availableThemes: ThemeDefinition[] = [];
  currentTheme: ThemeDefinition;
  private destroy$ = new Subject<void>();

  constructor(private themeService: ThemeService) { }

  ngOnInit(): void {
    this.availableThemes = this.themeService.getAvailableThemes();

    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        this.currentTheme = theme;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectTheme(theme: ThemeDefinition): void {
    this.themeService.setTheme(theme.name);
  }

  isCurrentTheme(theme: ThemeDefinition): boolean {
    return this.currentTheme?.name === theme.name;
  }
}
